import React, { Component } from 'react';
import { Link,Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default class Part10 extends Component {

    render() {

        const userToken = localStorage.getItem('token');
        if (!userToken) {
            return <Navigate to='/login' replace="true" />
        }

        return (
            <div className="docs-content">
                <div className="container">

                    <article className="docs-article mb-5">
                        <h1 className="docs-heading mb-4">PSP Capability Matrix Overview</h1>

                        <p>
                            This matrix defines what each PSP can do, how it must be integrated,
                            how it behaves at runtime, and how it maps to the canonical payment contract.
                        </p>

                        <p>
                            It is a system of record that describes capabilities, constraints, invariants, and risks for each PSP.
                        </p>

                        <p>This matrix is designed to:</p>
                        <ul className="doc-page">
                            <li>Drive routing and orchestration decisions</li>
                            <li>Enforce compliance and data requirements</li>
                            <li>Prevent documentation drift</li>
                            <li>Support auditability and reproducibility</li>
                        </ul>

                        <h5 className="mt-4">Canonical Reference Model</h5>
                        <ul className="doc-page">
                            <li><strong>1.1 Canonical Intents:</strong> DISCOVERY, FIELD_REQUIREMENTS, VALIDATE_ACCOUNT, FX_QUOTE, CREATE, CONFIRM/COMMIT, STATUS, WEBHOOK_IN, REFUND/REVERSAL/CHARGEBACK</li>
                            <li><strong>1.2 Canonical Rails:</strong> BANK_ACCOUNT, MOBILE_WALLET, CASH_PICKUP, CARD (pay-in), CARD_TO_CARD (payout), CRYPTO</li>
                            <li><strong>1.3 Canonical Lifecycle States:</strong> created → validated → quoted → authorized → submitted → pending → completed; terminal/side states: rejected, failed, cancelled, expired, reversed/refunded</li>
                            <li><strong>1.4 Canonical Compliance Dimensions:</strong> relationship, purpose, source_of_funds, KYC level, sanctions/PEP screening, country- and rail-specific rules, policy snapshot hash (routing + config + adapter version)</li>
                        </ul>

                        {/* ======================= */}
                        {/* PSP Coverage Matrix */}
                        {/* ======================= */}
                        <h5 className="mt-4">PSP Coverage Matrix — Intents and Capabilities</h5>
                        <div className="table-responsive mb-4">
                            <table className="table table-bordered table-sm doc-page">
                                <thead className="table-light">
                                    <tr>
                                        <th>PSP</th>
                                        <th>DISCOVERY</th>
                                        <th>FIELD_REQ</th>
                                        <th>VALIDATE</th>
                                        <th>FX_QUOTE</th>
                                        <th>CREATE</th>
                                        <th>CONFIRM</th>
                                        <th>STATUS</th>
                                        <th>WEBHOOK_IN</th>
                                        <th>REFUND / CB</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td>Adyen</td><td>No</td><td>No</td><td>No</td><td>Limited</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td></tr>
                                    <tr><td>Paysend</td><td>Limited</td><td>No</td><td>Limited</td><td>Yes (async)</td><td>Yes</td><td>Implicit</td><td>Yes (poll)</td><td>Not confirmed</td><td>No</td></tr>
                                    <tr><td>Lightnet</td><td>Yes</td><td>Yes</td><td>Partial</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Not confirmed</td><td>No</td></tr>
                                    <tr><td>Nsano</td><td>No</td><td>No</td><td>Yes</td><td>Implicit</td><td>Yes</td><td>No</td><td>Yes</td><td>Not confirmed</td><td>No</td></tr>
                                    <tr><td>Tranglo</td><td>Yes</td><td>Partial</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Not confirmed</td><td>No</td></tr>
                                    <tr><td>TerraPay</td><td>Yes</td><td>No</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Not confirmed</td><td>No</td></tr>
                                    <tr><td>Belmoney</td><td>Partial</td><td>Partial</td><td>Partial</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Not confirmed</td><td>No</td></tr>
                                    <tr><td>MSF (middleware)</td><td>No</td><td>No</td><td>No</td><td>No</td><td>Yes</td><td>No</td><td>Yes</td><td>No</td><td>No</td></tr>
                                    <tr><td>Shift</td><td>Yes</td><td>No</td><td>No</td><td>Yes</td><td>Yes</td><td>Partial</td><td>Yes</td><td>No</td><td>No</td></tr>
                                </tbody>
                            </table>
                            <p><em>“Partial” = capability exists but not first-class API; “Implicit” = behavior embedded in CREATE/STATUS flows.</em></p>
                        </div>

                        {/* ======================= */}
                        {/* Rail Matrix */}
                        {/* ======================= */}
                        <h5 className="mt-4">Rail and Instrument Support Matrix</h5>
                        <div className="table-responsive mb-4">
                            <table className="table table-bordered table-sm doc-page">
                                <thead className="table-light">
                                    <tr>
                                        <th>PSP</th>
                                        <th>Card Pay-in</th>
                                        <th>Card→Card</th>
                                        <th>Bank Account</th>
                                        <th>Mobile Wallet</th>
                                        <th>Cash Pickup</th>
                                        <th>Crypto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td>Adyen</td><td>Yes</td><td>No</td><td>No</td><td>No</td><td>No</td><td>No</td></tr>
                                    <tr><td>Paysend</td><td>No</td><td>Yes (tokenized)</td><td>Yes</td><td>Limited</td><td>No</td><td>No</td></tr>
                                    <tr><td>Lightnet</td><td>No</td><td>No</td><td>Yes</td><td>Yes</td><td>Yes</td><td>No</td></tr>
                                    <tr><td>Nsano</td><td>No</td><td>No</td><td>Yes</td><td>Yes</td><td>No</td><td>No</td></tr>
                                    <tr><td>Tranglo</td><td>No</td><td>No</td><td>Yes</td><td>Yes</td><td>Yes</td><td>No</td></tr>
                                    <tr><td>TerraPay</td><td>No</td><td>No</td><td>Yes</td><td>Yes</td><td>No</td><td>No</td></tr>
                                    <tr><td>Belmoney</td><td>No</td><td>No</td><td>Yes</td><td>Yes</td><td>Limited</td><td>No</td></tr>
                                    <tr><td>MSF</td><td>No</td><td>Limited</td><td>Yes</td><td>Yes</td><td>No</td><td>No</td></tr>
                                    <tr><td>Shift</td><td>No</td><td>No</td><td>No</td><td>No</td><td>No</td><td>Yes</td></tr>
                                </tbody>
                            </table>
                            <p><em>Operational constraint: Any PSP that touches raw card data must be isolated behind PCI-scoped boundary; orchestrator must never accept raw PAN data.</em></p>
                        </div>

                        {/* ======================= */}
                        {/* Authentication */}
                        {/* ======================= */}
                        <h5 className="mt-4">Authentication, Signing, and Transport Characteristics</h5>
                        <div className="table-responsive mb-4">
                            <table className="table table-bordered table-sm doc-page">
                                <thead className="table-light">
                                    <tr>
                                        <th>PSP</th>
                                        <th>Protocol</th>
                                        <th>Auth Type</th>
                                        <th>Signature Inputs</th>
                                        <th>Timestamp / Nonce</th>
                                        <th>Req Format</th>
                                        <th>Resp Format</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td>Adyen</td><td>REST</td><td>API key + Basic</td><td>None</td><td>No</td><td>JSON</td><td>JSON</td></tr>
                                    <tr><td>Paysend</td><td>REST (action-based)</td><td>MD5/HMAC header</td><td>Body + secret</td><td>Sometimes</td><td>JSON</td><td>JSON</td></tr>
                                    <tr><td>Lightnet</td><td>REST</td><td>HMAC</td><td>method + URL + body + nonce + ts</td><td>Yes</td><td>JSON</td><td>JSON</td></tr>
                                    <tr><td>Nsano</td><td>REST</td><td>API key header</td><td>None</td><td>No</td><td>JSON</td><td>JSON</td></tr>
                                    <tr><td>Tranglo</td><td>REST</td><td>MD5 signature</td><td>op-specific concat</td><td>Sometimes</td><td>JSON</td><td>JSON</td></tr>
                                    <tr><td>TerraPay</td><td>REST</td><td>Header creds / Basic</td><td>creds + date</td><td>Yes</td><td>JSON</td><td>JSON / XML</td></tr>
                                    <tr><td>Belmoney</td><td>REST</td><td>HMAC token</td><td>key + secret + timestamp</td><td>Yes</td><td>JSON</td><td>JSON</td></tr>
                                    <tr><td>MSF</td><td>REST</td><td>Unknown</td><td>Unknown</td><td>Unknown</td><td>JSON</td><td>JSON</td></tr>
                                    <tr><td>Shift</td><td>GraphQL</td><td>Bearer JWT</td><td>None</td><td>No</td><td>GraphQL JSON</td><td>GraphQL JSON</td></tr>
                                </tbody>
                            </table>
                        </div>

                        {/* ======================= */}
                        {/* Idempotency & Async Model */}
                        {/* ======================= */}
                        <h5 className="mt-4">Idempotency, Async Model, and State Mapping</h5>
                        <div className="table-responsive mb-4">
                            <table className="table table-bordered table-sm doc-page">
                                <thead className="table-light">
                                    <tr>
                                        <th>PSP</th>
                                        <th>Provider Idempotency</th>
                                        <th>Canonical Idempotency</th>
                                        <th>Async Pattern</th>
                                        <th>Poll</th>
                                        <th>Webhook</th>
                                        <th>Staged Commit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td>Adyen</td><td>Yes</td><td>Mandatory</td><td>Hybrid</td><td>Limited</td><td>Yes</td><td>No</td></tr>
                                    <tr><td>Paysend</td><td>Limited</td><td>Mandatory</td><td>Async</td><td>Yes</td><td>Unclear</td><td>No</td></tr>
                                    <tr><td>Lightnet</td><td>Limited</td><td>Mandatory</td><td>Hybrid</td><td>Yes</td><td>Unclear</td><td>Yes</td></tr>
                                    <tr><td>Nsano</td><td>No</td><td>Mandatory</td><td>Hybrid</td><td>Yes</td><td>Unclear</td><td>No</td></tr>
                                    <tr><td>Tranglo</td><td>Limited</td><td>Mandatory</td><td>Hybrid</td><td>Yes</td><td>Unclear</td><td>Yes</td></tr>
                                    <tr><td>TerraPay</td><td>Limited</td><td>Mandatory</td><td>Hybrid</td><td>Yes</td><td>Unclear</td><td>Yes</td></tr>
                                    <tr><td>Belmoney</td><td>Limited</td><td>Mandatory</td><td>Hybrid</td><td>Yes</td><td>Unclear</td><td>Yes</td></tr>
                                    <tr><td>MSF</td><td>Unknown</td><td>Mandatory</td><td>Poll-based</td><td>Yes</td><td>No</td><td>No</td></tr>
                                    <tr><td>Shift</td><td>Yes</td><td>Mandatory</td><td>Async</td><td>Yes</td><td>No</td><td>Partial</td></tr>
                                </tbody>
                            </table>
                            <p><em>Every transaction must have: canonical_idempotency_key, provider_reference, provider_request_hash, provider_response_hash. Retries must be idempotent by design.</em></p>
                        </div>

                        {/* ======================= */}
                        {/* Discovery & Dynamic Fields */}
                        {/* ======================= */}
                        <h5 className="mt-4">Discovery and Dynamic Field Support</h5>
                        <div className="table-responsive mb-4">
                            <table className="table table-bordered table-sm doc-page">
                                <thead className="table-light">
                                    <tr>
                                        <th>PSP</th>
                                        <th>Bank / Wallet Lists</th>
                                        <th>Dynamic Field API</th>
                                        <th>Regex / Length</th>
                                        <th>Country-Aware</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td>Lightnet</td><td>Yes</td><td>Yes (GetFieldInfo)</td><td>Yes</td><td>Yes</td></tr>
                                    <tr><td>Tranglo</td><td>Yes</td><td>Partial (lists)</td><td>No</td><td>Yes</td></tr>
                                    <tr><td>TerraPay</td><td>Yes</td><td>No</td><td>No</td><td>Yes</td></tr>
                                    <tr><td>Nsano</td><td>No</td><td>No</td><td>No</td><td>Yes</td></tr>
                                    <tr><td>Paysend</td><td>Limited</td><td>No</td><td>No</td><td>Yes</td></tr>
                                    <tr><td>Belmoney</td><td>Partial</td><td>Partial</td><td>Partial</td><td>Yes</td></tr>
                                </tbody>
                            </table>
                            <p><em>Dynamic PSP field definitions must be normalized into the canonical schema and versioned. UI forms must never be PSP-specific; they are rendered from canonical metadata.</em></p>
                        </div>

                        {/* ======================= */}
                        {/* Compliance & Regulatory */}
                        {/* ======================= */}
                        <h5 className="mt-4">Compliance and Regulatory Dimensions</h5>
                        <div className="table-responsive mb-4">
                            <table className="table table-bordered table-sm doc-page">
                                <thead className="table-light">
                                    <tr>
                                        <th>PSP</th>
                                        <th>Relationship Req</th>
                                        <th>Purpose Req</th>
                                        <th>SOF Req</th>
                                        <th>ID Types</th>
                                        <th>KYC Depth</th>
                                        <th>Sanctions / PEP</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td>Adyen</td><td>No</td><td>No</td><td>No</td><td>Limited</td><td>PSP-managed</td><td>PSP</td></tr>
                                    <tr><td>Paysend</td><td>Country-specific</td><td>Country-specific</td><td>Yes</td><td>Limited</td><td>Mixed</td><td>Mixed</td></tr>
                                    <tr><td>Lightnet</td><td>Yes (some corridors)</td><td>Yes</td><td>Yes</td><td>PSP-defined</td><td>Mixed</td><td>Mixed</td></tr>
                                    <tr><td>Nsano</td><td>Limited</td><td>Limited</td><td>Limited</td><td>Limited</td><td>PSP</td><td>PSP</td></tr>
                                    <tr><td>Tranglo</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Mixed</td><td>Mixed</td></tr>
                                    <tr><td>TerraPay</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Mixed</td><td>Mixed</td></tr>
                                    <tr><td>Belmoney</td><td>Yes</td><td>Yes</td><td>Yes</td><td>Yes</td><td>High</td><td>Mixed</td></tr>
                                    <tr><td>MSF</td><td>Unknown</td><td>Unknown</td><td>Unknown</td><td>Unknown</td><td>Unknown</td><td>Unknown</td></tr>
                                    <tr><td>Shift</td><td>No</td><td>No</td><td>No</td><td>N/A</td><td>Platform</td><td>Platform</td></tr>
                                </tbody>
                            </table>
                            <p><em>Canonical compliance fields are mandatory even if the PSP does not require them.</em></p>
                        </div>

                        {/* ======================= */}
                        {/* Settlement */}
                        {/* ======================= */}
                        <h5 className="mt-4">Settlement and Financial Semantics</h5>
                        <div className="table-responsive mb-4">
                            <table className="table table-bordered table-sm doc-page">
                                <thead className="table-light">
                                    <tr>
                                        <th>PSP</th>
                                        <th>Settlement Model</th>
                                        <th>Prefunding</th>
                                        <th>Ledger Dependency</th>
                                        <th>Reconciliation Source</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td>Adyen</td><td>T+N acquiring</td><td>No</td><td>Yes</td><td>Webhook + reports</td></tr>
                                    <tr><td>Paysend</td><td>Balance-based</td><td>Yes</td><td>Yes</td><td>Status API</td></tr>
                                    <tr><td>Lightnet</td><td>Prefund / hybrid</td><td>Yes</td><td>Yes</td><td>Status API</td></tr>
                                    <tr><td>Nsano</td><td>Prefund</td><td>Yes</td><td>Yes</td><td>Status API</td></tr>
                                    <tr><td>Tranglo</td><td>Prefund</td><td>Yes</td><td>Yes</td><td>Status API</td></tr>
                                    <tr><td>TerraPay</td><td>Prefund</td><td>Yes</td><td>Yes</td><td>Status API</td></tr>
                                    <tr><td>Belmoney</td><td>Prefund</td><td>Yes</td><td>Yes</td><td>Status API</td></tr>
                                    <tr><td>MSF</td><td>Unknown</td><td>Unknown</td><td>Yes</td><td>Poll</td></tr>
                                    <tr><td>Shift</td><td>Exchange ledger</td><td>Yes</td><td>Yes</td><td>GraphQL queries</td></tr>
                                </tbody>
                            </table>
                        </div>

                        {/* ======================= */}
                        {/* Operational Risk Notes */}
                        {/* ======================= */}
                        <h5 className="mt-5">Operational Risk Notes</h5>
                        <div className="card mb-4 shadow-sm">
                            <div className="card-body doc-page">
                                <ul className="mb-2">
                                    <li>Max polling duration</li>
                                    <li>Retryable vs terminal error codes</li>
                                    <li>Known transient failures</li>
                                    <li>Rate-limit behavior</li>
                                    <li>Sandbox vs production divergences</li>
                                    <li>Dependency on middleware or third parties</li>
                                    <li>Rollback strategy on partial failure</li>
                                </ul>
                                <p className="text-muted mb-0">
                                    <em><strong>Absence of this data means the PSP is not production-ready.</strong></em>
                                </p>
                            </div>
                        </div>

                        {/* ======================= */}
                        {/* Required Machine-Readable Capability Record */}
                        {/* ======================= */}
                        <h5 className="mt-5">Required Machine-Readable Capability Record</h5>
                        <div className="card mb-4 shadow-sm">
                            <div className="card-body doc-page">
                                <ul className="mb-2">
                                    <li>intents_supported[]</li>
                                    <li>rails_supported[]</li>
                                    <li>corridors_supported[]</li>
                                    <li>auth_spec</li>
                                    <li>signature_spec</li>
                                    <li>async_model</li>
                                    <li>idempotency_model</li>
                                    <li>compliance_requirements</li>
                                    <li>dynamic_field_support</li>
                                    <li>settlement_model</li>
                                    <li>retry_policy</li>
                                    <li>error_taxonomy</li>
                                    <li>operational_limits</li>
                                    <li>adapter_version</li>
                                    <li>policy_snapshot_hash</li>
                                </ul>
                                <p className="text-muted mb-0">
                                    <em><strong>Consumed by the routing engine, validation layer, UI schema renderer, and audit/export tooling.</strong></em>
                                </p>
                            </div>
                        </div>

                    </article>

                    <article class="docs-article" id="section-3">
                        <section class="docs-section row" id="item-3-1">
                            <div class="col-12">
                                <nav aria-label="Page navigation example">
                                    <ul class="pagination justify-content-between">
                                        <li class="page-item">
                                            <Link class="page-link" to="/part-nine-doc" tabIndex="-1"
                                                aria-disabled="true"><i class="bi bi-arrow-left"></i> &nbsp;
                                                Part IX
                                            </Link>
                                        </li>
                                        <li class="page-item">
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </section>
                    </article>

                </div>

                <ToastContainer closeOnClick autoClose={2000} />
            </div>
        )
    }
}